/* eslint-disable no-param-reassign */
import { ActionReducerMapBuilder, createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

import DefaultApiLimit from '../../config/defaultLimit';
import { FirmwareJob, RequestFirmwareJobs } from '../../interface/firmware-jobs.interface';
import { removeEmptyKeys } from '../../shared-functions/DateTime';
import firmwareJobsApi from '../../utils/api/firmware-jobs';

import { setNotificationMessage } from './NotificationManagementSlice';

export interface FirmwareJobsResponse {
  results: FirmwareJob[];
  page: number;
  limit: number;
  totalPages: number;
  totalResults: number;
}

export interface FirmwareJobsState {
  jobs: FirmwareJob[];
  limit: number;
  page: number;
  totalPages: number;
  totalResults: number;
  loading: boolean;
  sortBy: 'asc' | 'desc' | null;
  sortField: string | null;
}

const initialState: FirmwareJobsState = {
  jobs: [],
  limit: DefaultApiLimit.limit,
  page: DefaultApiLimit.page,
  totalPages: 0,
  totalResults: 0,
  loading: false,
  sortBy: null,
  sortField: null,
};

export const fetchFirmwareJobs = createAsyncThunk<FirmwareJobsResponse, RequestFirmwareJobs>(
  'firmwareJobs/fetchFirmwareJobs',
  async (data: RequestFirmwareJobs, { dispatch }) => {
    try {
      const response = await firmwareJobsApi.fetchFirmwareJobs(removeEmptyKeys(data));
      return response.data;
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Something went wrong!',
          code: error?.response?.data?.code || 200,
          status: true,
        }),
      );
      throw error;
    }
  },
);

export const refreshFirmwareJobs = createAsyncThunk<FirmwareJobsResponse, RequestFirmwareJobs>(
  'firmwareJobs/refreshFirmwareJobs',
  async (data: RequestFirmwareJobs) => {
    const response = await firmwareJobsApi.fetchFirmwareJobs(removeEmptyKeys(data));
    return response.data;
  },
);

export const FirmwareJobsSlice = createSlice({
  name: 'firmwareJobs',
  initialState,
  reducers: {
    /**
     * Function for sorting data by field
     * @param {FirmwareJobsState} state - The current state.
     * @param {PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>} action - The action.
     */
    setSortData: (state, action: PayloadAction<{ sortField: string; sortBy: 'asc' | 'desc' }>) => {
      state.sortField = action.payload.sortField;
      state.sortBy = action.payload.sortBy;
    },
  },
  /**
   * Extra reducers
   * @param {ActionReducerMapBuilder<FirmwareJobsState>} builder - The map builder
   */
  extraReducers: (builder: ActionReducerMapBuilder<FirmwareJobsState>) => {
    builder
      .addCase(fetchFirmwareJobs.pending, (state) => {
        state.loading = true;
      })
      .addCase(
        fetchFirmwareJobs.fulfilled,
        (state: FirmwareJobsState, action: PayloadAction<FirmwareJobsResponse>) => {
          state.loading = false;
          state.jobs = action.payload.results.map((job) => ({ ...job, id: job._id }));
          state.limit = action.payload.limit;
          state.page = action.payload.page;
          state.totalPages = action.payload.totalPages;
          state.totalResults = action.payload.totalResults;
        },
      )
      .addCase(fetchFirmwareJobs.rejected, (state) => {
        state.loading = false;
      });

    builder.addCase(
      refreshFirmwareJobs.fulfilled,
      (state: FirmwareJobsState, action: PayloadAction<FirmwareJobsResponse>) => {
        state.jobs = action.payload.results.map((job) => ({ ...job, id: job._id }));
        state.limit = action.payload.limit;
        state.page = action.payload.page;
        state.totalPages = action.payload.totalPages;
        state.totalResults = action.payload.totalResults;
      },
    );
  },
});

export const { setSortData } = FirmwareJobsSlice.actions;

export default FirmwareJobsSlice.reducer;
